import React, { useState } from 'react';
import axios from 'axios';
import { Download, Loader2, CheckCircle, FileText, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email-gated lead magnet: the doctor-written PMOS Nutrition & Lifestyle Guide.
 * Saves the lead, downloads the PDF instantly, and (when Resend is live) emails it.
 */
const LeadMagnet = ({ className = '' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/lead-magnet/pmos-guide`,
        { full_name: name, email },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'TCUBE-PMOS-Nutrition-Lifestyle-Guide.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDone(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-900/30 to-cyan-900/20 p-6 sm:p-8 ${className}`}
      data-testid="lead-magnet"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-medium mb-3">
            <FileText className="w-3.5 h-3.5" aria-hidden="true" /> Free Doctor-Written Guide
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
            PMOS (previously called PCOS) Nutrition &amp; Lifestyle Guide
          </h3>
          <p className="text-slate-300 text-sm sm:text-base mb-3 leading-relaxed">
            A clinical, evidence-based guide covering what PMOS is, why nutrition and lifestyle matter,
            core principles, and when to seek medical care, written and reviewed by our medical team.
          </p>
          <p className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" aria-hidden="true" />
            Reviewed by Dr. Pooja Sree (MBBS, DNB General Medicine)
          </p>
        </div>

        <div className="md:w-80 flex-shrink-0">
          {done ? (
            <div className="text-center py-4" data-testid="lead-magnet-success">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" aria-hidden="true" />
              <p className="text-white font-semibold">Your guide is downloading.</p>
              <p className="text-slate-400 text-sm mt-1">A copy is also on its way to your inbox.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
                data-testid="lead-magnet-name"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                placeholder="Your email address"
                required
                className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
                data-testid="lead-magnet-email"
              />
              {error && <p className="text-rose-400 text-xs" data-testid="lead-magnet-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-60 text-white px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-lg shadow-blue-500/25"
                data-testid="lead-magnet-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {loading ? 'Preparing your guide…' : 'Get the Free Guide'}
              </button>
              <p className="text-slate-500 text-[11px] text-center">We respect your privacy. No spam.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadMagnet;
